import React from "react";
import { withTranslation, Trans } from "react-i18next";
import PropTypes from "prop-types";
import Notification from "@tds/core-notification";
import Text from "@tds/core-text";
import Link from "@tds/core-link";
import NativeHTML from "../nativeHTML/NativeHTML";

/**
 * A functional component used to display banner messages. Typically used for Error messaging.
 *
 * @param {Object} props See PropTypes below.
 * @return {React.FC} The message.
 *
 * Messages that contain message ids (e.g., 1004) are given highest priority followed by messages
 * that do not contain message ids but do contain display text. Lastly, Messages that do not contain
 * message id or display test are assigned a fallback message id based on status code (e.g., 404).
 */
const Message = (props) => {
  const { message, dismissible, locale, t } = props;

  // Make a copy of the message prop since we may need to overwrite some properties.
  const messageCopy = { ...message };
  // If server sends status code but doesn't send error details, use a fallback messageId.
  if (!message.messageId && !message.displayedText) {
    messageCopy.messageId = `fallback${message.status}`;
  }

  // Ensure variant is one of the valid options of tds Notification. Applys to new message component only.
  const variant = ["branded", "warning", "error", "success"].some(
    (e) => e === t(`messages.${messageCopy.messageId}.type`)
  )
    ? t(`messages.${messageCopy.messageId}.type`)
    : "error";

  return (
    <>
      {/* NEW MESSAGE COMPONENT USING CLIENT SIDE TRANSLATED ERROR MESSAGES BASED ON MESSAGE ID. */}
      {messageCopy.messageId && (
        <Notification variant={variant} dismissible={dismissible} copy={locale}>
          <Text>
            <Trans
              // Translate mapped message. Returns blank string if msg Id doesn't exist and to block render.
              defaults={t(
                `messages.${messageCopy.messageId}.message`,
                `${messageCopy.displayedText}`
              )}
              // TdsLink is currently the only node type in the translations. Add as necessary.
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              components={{ TdsLink: <Link /> }}
            />
          </Text>
        </Notification>
      )}
      {/* ORIGINAL MESSAGE COMPONENT FOR USE WHEN MESSAGE ID IS NOT AVAILABLE. */}
      {!message.messageId && message.displayedText && (
        <Notification variant="error" dismissible={dismissible} copy={locale}>
          <NativeHTML
            content={message.displayedText}
            className="notification"
          />
        </Notification>
      )}
    </>
  );
};

Message.propTypes = {
  /* Message prop object contains properties used to determine how messages are displayed */
  message: PropTypes.shape({
    name: PropTypes.string,
    status: PropTypes.string,
    displayedText: PropTypes.string,
    messageId: PropTypes.string,
  }),
  /* Dismissible prop is used to make message dismissible or not */
  dismissible: PropTypes.bool,
  /* Locale prop used to determine message language */
  locale: PropTypes.oneOf(["en", "fr"]).isRequired,
  /* Translation function from i18next used to translate text based on locale */
  t: PropTypes.func.isRequired,
};

Message.defaultProps = {
  message: {
    name: "",
    status: "",
    displayedText: "",
    messageId: "",
  },
  dismissible: false,
};
export default withTranslation()(Message);
